import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { CameraScanner } from '../../utils/cameraScan'

/**
 * Camera-based barcode scanning modal
 * Shows camera preview with scanning overlay and detects IATA barcodes
 */
class CameraScanModal extends Component {
  static propTypes = {
    show: PropTypes.bool.isRequired,
    onScanSuccess: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
  }

  state = {
    isInitializing: true,
    isScanning: false,
    error: null,
    permissionDenied: false,
  }

  scanner = null
  videoRef = null

  async componentDidMount() {
    if (this.props.show) {
      await this.initializeScanner()
    }
  }

  async componentDidUpdate(prevProps) {
    // Start scanner when modal becomes visible
    if (!prevProps.show && this.props.show) {
      await this.initializeScanner()
    }
    // Cleanup when modal becomes hidden
    if (prevProps.show && !this.props.show) {
      this.cleanup()
    }
  }

  componentWillUnmount() {
    this.cleanup()
  }

  /**
   * Initialize camera scanner and start scanning
   */
  initializeScanner = async () => {
    try {
      this.scanner = new CameraScanner()
      await this.scanner.initialize()

      this.setState({ isInitializing: false })

      // Let cameraScan.js use facingMode: 'environment' to select back camera
      this.startScanning(null)
    } catch (err) {
      console.error('Initialize scanner error:', err)
      this.handleError(err)
    }
  }

  /**
   * Start camera scanning
   */
  startScanning = async (deviceId) => {
    this.setState({ isScanning: true })

    try {
      console.log('Starting camera scan with deviceId:', deviceId)
      console.log('Video element ready?', this.videoRef ? 'yes' : 'no')

      if (!this.videoRef) {
        console.error('Video element not ready, waiting...')
        setTimeout(() => this.startScanning(deviceId), 100)
        return
      }

      await this.scanner.startScanning(
        this.videoRef,
        deviceId,
        this.handleScanSuccess,
        this.handleScanError,
      )
    } catch (err) {
      console.error('Failed to start scanning:', err)
      this.handleError(err)
    }
  }

  /**
   * Handle successful barcode detection
   */
  handleScanSuccess = (barcode) => {
    this.cleanup()
    this.props.onScanSuccess(barcode)
  }

  /**
   * Handle scanning errors (non-fatal)
   */
  handleScanError = (error) => {
    console.error('Scan error:', error)
    this.handleError(error)
  }

  /**
   * Handle initialization errors
   */
  handleError = (err) => {
    let errorMessage = 'Unable to access camera'
    let permissionDenied = false

    if (
      err.name === 'NotAllowedError' ||
      err.name === 'PermissionDeniedError'
    ) {
      errorMessage = 'Camera permission denied'
      permissionDenied = true
    } else if (err.name === 'NotFoundError') {
      errorMessage = 'No camera found on this device'
    } else if (err.name === 'NotReadableError') {
      errorMessage = 'Camera is already in use by another application'
    } else if (err.name === 'NotSupportedError') {
      errorMessage = 'Camera access not supported in this browser'
    }

    this.setState({
      error: errorMessage,
      permissionDenied,
      isInitializing: false,
      isScanning: false,
    })
  }

  /**
   * Clean up camera resources
   */
  cleanup = () => {
    if (this.scanner) {
      this.scanner.dispose()
      this.scanner = null
    }
  }

  /**
   * Handle cancel button click
   */
  handleCancel = () => {
    this.cleanup()
    this.props.onCancel()
  }

  render() {
    const { isInitializing, isScanning, error, permissionDenied } = this.state

    if (!this.props.show) {
      return null
    }

    return (
      <div className="fixed inset-0 z-50 bg-black flex flex-col">
        {/* Camera Video Stream */}
        <div className="flex-1 relative overflow-hidden">
          <video
            ref={(ref) => (this.videoRef = ref)}
            className="w-full h-full object-cover"
            autoPlay
            playsInline
            muted
          />

          {/* Scanning Overlay */}
          {isScanning && !error && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-full max-w-md">
                {/* Scan Frame */}
                <div className="relative border-4 border-green-500 rounded-lg h-48 shadow-[0_0_0_9999px_rgba(0,0,0,0.5)]">
                  {/* Corner indicators */}
                  <div className="absolute top-0 left-0 w-8 h-8 border-l-4 border-t-4 border-white"></div>
                  <div className="absolute top-0 right-0 w-8 h-8 border-r-4 border-t-4 border-white"></div>
                  <div className="absolute bottom-0 left-0 w-8 h-8 border-l-4 border-b-4 border-white"></div>
                  <div className="absolute bottom-0 right-0 w-8 h-8 border-r-4 border-b-4 border-white"></div>
                </div>

                {/* Instructions */}
                <p className="text-white text-center mt-4 text-lg font-medium">
                  Position barcode within frame
                </p>
                <p className="text-gray-300 text-center mt-2">
                  Detection will happen automatically
                </p>
              </div>
            </div>
          )}

          {/* Loading State */}
          {isInitializing && (
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-75">
              <div className="text-center">
                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-white mx-auto"></div>
                <p className="text-white mt-4 text-lg">
                  Initializing camera...
                </p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-90">
              <div className="text-center px-6 max-w-md">
                <div className="text-red-500 text-6xl mb-4">⚠</div>
                <h3 className="text-white text-xl font-bold mb-2">
                  Camera Error
                </h3>
                <p className="text-gray-300 mb-4">{error}</p>

                {permissionDenied && (
                  <div className="bg-gray-800 rounded-lg p-4 mt-4">
                    <p className="text-sm text-gray-300">
                      To enable camera access:
                    </p>
                    <ul className="text-sm text-gray-400 mt-2 text-left list-disc list-inside">
                      <li>
                        Check your browser's address bar for camera permissions
                      </li>
                      <li>
                        Go to browser settings and allow camera access for this
                        site
                      </li>
                      <li>Reload the page after granting permission</li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Bottom Controls */}
        <div className="p-4 bg-gray-900">
          <button
            onClick={this.handleCancel}
            className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg text-lg transition-colors min-h-[48px]"
          >
            Cancel
          </button>
        </div>
      </div>
    )
  }
}

export default CameraScanModal
